import React, { useCallback, useState } from 'react';
import {
  Linking, Platform, ScrollView, StyleSheet, Text, TextInput,
  TouchableOpacity, View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ActionLoader from '../../components/ActionLoader';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTheme } from '../../context/ThemeContext';
import { getThemeColors } from '../../utils/themeStyles';
import { useAuth } from '../../context/AuthContext';
import { orgApi, OrgSummary } from '../../api/org';
import { getBoUrl, getStudioUrl, getBoUrlFor, BO_SHORTCUTS } from '../../config/env';
import { useT } from '../../i18n/useT';

function openExternal(url: string) {
  if (!url) return;
  if (Platform.OS === 'web' && typeof window !== 'undefined') window.open(url, '_blank');
  else void Linking.openURL(url);
}

export default function CompanyAdminScreen() {
  const t = useT();
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { getAccessToken } = useAuth();
  const [org, setOrg] = useState<OrgSummary | null>(null);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Creation form.
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { setError(t('company.notSignedIn')); setLoading(false); return; }
    try {
      const [o, inv] = await Promise.all([orgApi.myOrg(token), orgApi.myInvitations(token)]);
      setOrg(o); setPendingInvites(inv.invitations?.length ?? 0); setError(null);
    } catch (e: any) { setError(t('company.loadFailed')); }
    finally { setLoading(false); }
  }, [getAccessToken, t]);

  useFocusEffect(useCallback(() => { setLoading(true); load(); }, [load]));

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await orgApi.createCompany(getAccessToken(), {
        name: name.trim(), slug: slug.trim() || undefined, domain: domain.trim() || undefined,
      });
      setName(''); setSlug(''); setDomain(''); setShowForm(false);
      await load();
    } catch (e) { setError(t('company.createFailed')); }
    finally { setSaving(false); }
  };

  // Company lifecycle (admin): archive / reactivate / delete.
  const [lifeBusy, setLifeBusy] = useState<number | null>(null);
  const lifecycle = async (companyid: number, action: 'archive' | 'reactivate' | 'delete', confirmMsg?: string) => {
    if (confirmMsg && Platform.OS === 'web' && typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;
    setLifeBusy(companyid); setError(null);
    try {
      if (action === 'archive') await orgApi.archiveCompany(getAccessToken(), companyid);
      else if (action === 'reactivate') await orgApi.reactivateCompany(getAccessToken(), companyid);
      else await orgApi.deleteCompany(getAccessToken(), companyid);
      await load();
    } catch { setError(t('company.actionFailed')); }
    finally { setLifeBusy(null); }
  };

  const caps = org?.capabilities;
  const boUrl = getBoUrl();
  const studioUrl = getStudioUrl();

  return (
    <SafeAreaView style={[s.container, { backgroundColor: colors.background }]}>
      <ScrollView contentContainerStyle={s.scroll}>
        <Text style={[s.title, { color: colors.textPrimary }]}>{t('company.title')}</Text>

        {loading && <View style={{ marginTop: 20 }}><ActionLoader label={t('company.loading')} /></View>}
        {error && <Text style={[s.err, { color: colors.error }]}>{error}</Text>}

        {!loading && org && (
          <>
            {/* Back-office role → shortcut menu into the back-office (deep-links) */}
            {caps?.boAdminLink && boUrl !== '' && (
              <View style={{ marginBottom: 16 }}>
                <View style={s.linksRow}>
                  <TouchableOpacity style={[s.linkBtn, { backgroundColor: colors.primary }]} onPress={() => openExternal(boUrl)}>
                    <Ionicons name="briefcase" size={16} color="#FFF" />
                    <Text style={s.linkBtnText}>{t('company.openBackOffice')}</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[s.shortcutsTitle, { color: colors.textSecondary }]}>{t('company.shortcuts')}</Text>
                <View style={s.shortcutsGrid}>
                  {BO_SHORTCUTS.filter((sc) => getBoUrlFor(sc.path) !== '').map((sc) => (
                    <TouchableOpacity key={sc.path} style={[s.shortcutChip, { borderColor: colors.border, backgroundColor: colors.cardBackground }]}
                      onPress={() => openExternal(getBoUrlFor(sc.path))}>
                      <Ionicons name={sc.icon as any} size={15} color={colors.primary} />
                      <Text style={[s.shortcutText, { color: colors.textPrimary }]}>{sc.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
            {/* Author without full back-office access: the Studio shortcut alone */}
            {!caps?.boAdminLink && caps?.studioLink && studioUrl !== '' && (
              <View style={s.linksRow}>
                <TouchableOpacity style={[s.linkBtn, { backgroundColor: '#7C3AED' }]} onPress={() => openExternal(studioUrl)}>
                  <Ionicons name="color-wand" size={16} color="#FFF" />
                  <Text style={s.linkBtnText}>{t('company.studio')}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Pending invitations */}
            {pendingInvites > 0 && (
              <TouchableOpacity
                style={[s.inviteBanner, { backgroundColor: colors.cardBackground, borderColor: colors.primary }]}
                onPress={() => navigation.navigate('MyInvitations')}>
                <Ionicons name="mail-unread" size={20} color={colors.primary} />
                <Text style={[s.inviteText, { color: colors.textPrimary }]}>
                  {t('company.pendingInvites', { count: pendingInvites })}
                </Text>
                <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />
              </TouchableOpacity>
            )}

            {/* My companies */}
            <Text style={[s.sectionTitle, { color: colors.textSecondary }]}>{t('company.mine')}</Text>
            {org.companies.length === 0 && (
              <Text style={[s.hint, { color: colors.textTertiary }]}>{t('company.none')}</Text>
            )}
            {org.companies.map((c) => {
              const isAdmin = c.roles.includes('company-admin');
              const pending = c.status === 'pending_activation';
              const inactive = c.status === 'inactive';
              const archived = c.status === 'archived';
              const badge = pending ? { label: t('company.status.pendingActivation'), color: '#D97706' }
                : archived ? { label: t('company.status.archived'), color: '#D97706' }
                : inactive ? { label: t('company.status.inactive'), color: '#DC2626' } : null;
              // Only the companies you administer are tappable (management).
              const Wrapper: any = isAdmin ? TouchableOpacity : View;
              const wrapperProps = isAdmin
                ? { activeOpacity: 0.75, onPress: () => navigation.navigate('CompanyDetail', { companyId: c.companyid, name: c.name }) }
                : {};
              const showLifecycle = isAdmin && (c.status === 'active' || archived);
              return (
                <React.Fragment key={c.companyid}>
                  <Wrapper {...wrapperProps} style={[s.card, { backgroundColor: colors.cardBackground }, showLifecycle && { marginBottom: 6 }]}>
                    <View style={[s.iconCircle, { backgroundColor: isAdmin ? '#6366F1' : pending ? '#D97706' : '#94A3B8' }]}>
                      <Ionicons name="business" size={20} color="#FFF" />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={[s.cardTitle, { color: colors.textPrimary }]}>{c.name}</Text>
                      <Text style={[s.cardSub, { color: colors.textSecondary }]}>{c.roles.join(', ') || t('company.member')}</Text>
                      {badge && (
                        <View style={[s.badge, { backgroundColor: badge.color + '22', borderColor: badge.color }]}>
                          <Text style={[s.badgeText, { color: badge.color }]}>{badge.label}</Text>
                        </View>
                      )}
                      {!isAdmin && pending && (
                        <Text style={{ color: colors.textTertiary, fontSize: 11, marginTop: 4 }}>
                          {t('company.accessAfterActivation')}
                        </Text>
                      )}
                    </View>
                    {isAdmin && <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />}
                  </Wrapper>
                  {showLifecycle && (
                    <View style={s.lifeRow}>
                      {archived ? (
                        <TouchableOpacity disabled={lifeBusy === c.companyid} onPress={() => lifecycle(c.companyid, 'reactivate')}
                          style={[s.lifeBtn, { borderColor: colors.primary }]}>
                          <Text style={[s.lifeTxt, { color: colors.primary }]}>{lifeBusy === c.companyid ? '…' : t('company.reactivate')}</Text>
                        </TouchableOpacity>
                      ) : (
                        <TouchableOpacity disabled={lifeBusy === c.companyid} onPress={() => lifecycle(c.companyid, 'archive')}
                          style={[s.lifeBtn, { borderColor: colors.border }]}>
                          <Text style={[s.lifeTxt, { color: colors.textSecondary }]}>{lifeBusy === c.companyid ? '…' : t('company.archive')}</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity disabled={lifeBusy === c.companyid}
                        onPress={() => lifecycle(c.companyid, 'delete', t('company.deleteConfirm', { name: c.name }))}
                        style={[s.lifeBtn, { borderColor: '#DC2626' }]}>
                        <Text style={[s.lifeTxt, { color: '#DC2626' }]}>{t('company.delete')}</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </React.Fragment>
              );
            })}

            {/* Creation */}
            {!showForm ? (
              <TouchableOpacity style={[s.createBtn, { borderColor: colors.primary }]} onPress={() => setShowForm(true)}>
                <Ionicons name="add-circle" size={18} color={colors.primary} />
                <Text style={[s.createBtnText, { color: colors.primary }]}>{t('company.create')}</Text>
              </TouchableOpacity>
            ) : (
              <View style={[s.formCard, { backgroundColor: colors.cardBackground }]}>
                <Text style={[s.sectionTitle, { color: colors.textSecondary, marginTop: 0 }]}>{t('company.createNew')}</Text>
                <Input c={colors} placeholder={t('company.field.name')} value={name} onChangeText={setName} />
                <Input c={colors} placeholder={t('company.field.slug')} value={slug} onChangeText={setSlug} />
                <Input c={colors} placeholder={t('company.field.domain')} value={domain} onChangeText={setDomain} />
                <Text style={[s.hint, { color: colors.textTertiary }]}>
                  {t('company.createProvisioning')}
                </Text>
                <View style={s.formActions}>
                  <TouchableOpacity onPress={() => setShowForm(false)} style={[s.ghostBtn, { borderColor: colors.border }]}>
                    <Text style={{ color: colors.textPrimary }}>{t('common.cancel')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={create} disabled={saving || !name.trim()}
                    style={[s.primaryBtn, { backgroundColor: name.trim() ? colors.primary : colors.border }]}>
                    <Text style={{ color: '#FFF', fontWeight: '700' }}>{saving ? t('company.creating') : t('common.create')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function Input({ c, ...props }: any) {
  return (
    <TextInput
      {...props}
      placeholderTextColor={c.inputPlaceholder}
      style={[s.input, { backgroundColor: c.inputBackground, borderColor: c.inputBorder, color: c.inputText }]}
    />
  );
}

const s = StyleSheet.create({
  container: { flex: 1 },
  scroll: { padding: 20, paddingBottom: 40 },
  title: { fontSize: 26, fontWeight: 'bold', marginBottom: 16 },
  err: { fontSize: 13, marginBottom: 12 },
  linksRow: { flexDirection: 'row', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  linkBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10 },
  linkBtnText: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  shortcutsTitle: { fontSize: 11, fontWeight: '700', letterSpacing: 0.5, marginTop: 4, marginBottom: 8 },
  shortcutsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  shortcutChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, borderWidth: 1 },
  shortcutText: { fontSize: 12, fontWeight: '600' },
  inviteBanner: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 12, borderWidth: 1, marginBottom: 16 },
  inviteText: { flex: 1, fontSize: 14, fontWeight: '600' },
  sectionTitle: { fontSize: 13, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 8, marginBottom: 10 },
  hint: { fontSize: 12, marginBottom: 8 },
  card: { flexDirection: 'row', alignItems: 'center', borderRadius: 12, padding: 14, marginBottom: 10, gap: 12 },
  iconCircle: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  cardTitle: { fontSize: 15, fontWeight: '700' },
  cardSub: { fontSize: 12, marginTop: 2 },
  badge: { alignSelf: 'flex-start', borderWidth: 1, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2, marginTop: 5 },
  badgeText: { fontSize: 10.5, fontWeight: '700' },
  lifeRow: { flexDirection: 'row', gap: 8, marginBottom: 10, marginLeft: 52 },
  lifeBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8, borderWidth: 1 },
  lifeTxt: { fontSize: 12, fontWeight: '600' },
  createBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1.5, borderRadius: 12, paddingVertical: 12, marginTop: 8 },
  createBtnText: { fontSize: 14, fontWeight: '700' },
  formCard: { borderRadius: 12, padding: 16, marginTop: 8 },
  input: { height: 40, borderRadius: 8, borderWidth: 1, paddingHorizontal: 10, fontSize: 14, marginBottom: 10 },
  formActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 6 },
  ghostBtn: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, borderWidth: 1 },
  primaryBtn: { paddingHorizontal: 18, paddingVertical: 10, borderRadius: 8 },
});
